import React, { Fragment } from 'react'
import { t, Trans } from '@lingui/macro'
import Link from '../../../components/common/navigation/Link'
import Accordion from '../../../components/common/Accordion'

import './FaqSection.scss'

const FAQ_CONTENT = [
  {
    title: <Trans>Where can I use Yoti?</Trans>,
    content: (
      <Trans>
        <p>
          Yoti is used across many industries for many different reasons around the world.
          A small section of this includes governments, national services, gambling and dating websites, convenience stores and supermarkets, night clubs, financial services to name a few.
        </p>
      </Trans>
    )
  },
  {
    title: <Trans>Why is Yoti the best method?</Trans>,
    content: (
      <Trans>
        <p>
          The Yoti app allows you to share just the information strictly necessary for a transaction.
        </p>
        <p>
          For example, to prove your age to buy alcohol in the UK, you can just share the fact that you’re over 18 and nothing else.
          If you were using a passport, you would have to share your photo, name and date of birth.
        </p>
        <p>
          We will have already verified your details against the ID document you used to open your Yoti,
          so the business can have confidence that the details shared are real and accurate without needing to have a copy of the ID document themselves.
          This protects you against identity fraud and means you don’t need to send ID documents insecurely via email.
        </p>
      </Trans>
    )
  },
  {
    title: <Trans>What happens to my data?</Trans>,
    content: (
      <Trans>
        <p>
          We have taken a radical new approach to protecting personal data. Instead of storing your information as a single record on one big database,
          we store each individual piece of your data separately.
        </p>
        <p>
          Imagine the Yoti database as a bank vault. Each piece of your data is split up, turned into unreadable data through encryption and stored in a different safe.
        </p>
        <p>
          Only you have the key to access these safes, which is stored on your phone and not on the Yoti database.
        </p>
        <p>
          When you unlock your app with your five-digit PIN, you activate your key which then pulls all of these individual pieces of data together and turns them back into readable text.
        </p>
        <p>
          For extra security, Yoti also encrypts your key. To gain access to your safes in the vault, your key must match our Yoti key.
        </p>
      </Trans>
    )
  },
  {
    title: <Trans>Is it a free service?</Trans>,
    content: (
      <Trans>
        <p>
          The Yoti app is free to download and use. We don’t think you should have to pay to prove your identity.
        </p>
        <p>
          We make money by charging some businesses a small fee to check the identity of their customers and employees.
        </p>
        <p>
          We will not sell your information to third parties for marketing or any other purpose. In fact, once your account is verified,
          we have no access to your information anyway. It is your information, and only you should choose who it is shared with.
        </p>
      </Trans>
    )
  },
  {
    title: <Trans>What kind of company is Yoti?</Trans>,
    content: (
      <Trans>
        <p>
          Just as the right to identity is a fundamental human right, privacy is too.
        </p>
        <p>
          We created Yoti to give everybody a secure, privacy-friendly way of proving their identity, online and in person.
          Privacy and security, therefore, aren’t just our priority but our raison d’etre.
        </p>
        <p>
          Our free Yoti app is built with privacy and security at its core and harnesses data minimisation techniques that enable you to share less data.
        </p>
        <p>
          We have a rigorous approach to security and have built an innovative database architecture designed to protect against data breaches or cybersecurity attacks.
        </p>
        <p>
          To ensure that we are held accountable, we are advised by our Guardian Council, an independent board of expert professionals and
          dedicated advisors from data privacy, human rights, online harms and last-mile technology sectors.
        </p>
        <p>
          Our mission is, and will forever be, to be the world’s trusted identity platform.
          This is not a journey we make on our own but with policy advisors, think tanks, researchers, academics and humanitarian bodies.
        </p>
        <p>
          As our sixth core business principle states, we are transparent about what we are doing and why, so in light of this transparency,
          we have answered your questions on how we protect your data.
        </p>
        <p>
          <span>We also encourage you to read our </span>
          <Link href="https://www.yoti.com/privacypolicy/">
            Privacy Policy
          </Link>
          <span> for all the ins and outs on how we handle your data.</span>
        </p>
      </Trans>
    )
  },
  {
    title: <Trans>How does scanning the QR code work?</Trans>,
    content: (
      <Trans>
        <p>
          ‘QR’ stands for ‘Quick Response’. A QR code looks like a pattern of black and white squares.
        </p>
        <p>
          It’s a type of two-dimensional barcode that you read using smartphones and dedicated QR reading devices. They link directly to text, emails, websites, phone numbers and more.
        </p>
        <p>
          We use QR codes to communicate important information for interacting with Yoti. When scanning a Yoti code,
          the scanning device captures this information and uses it to initiate a transaction, for example a login or initiating a share of identity information.
        </p>
        <p>
          We call our QR codes ‘Yoti codes’, you can easily recognise them by the Yoti logo in the centre. Yoti codes can only be read within the Yoti app.
        </p>
      </Trans>
    )
  }
]

const Faq = () => (
  <div className="faq">
    <div className="faq__container">
      <h4 className="faq__container__header"><Trans>Frequently asked questions</Trans></h4>
      <Accordion items={FAQ_CONTENT} autoClose />
    </div>
  </div>
)

export default Faq
